import React from "react"
import { Formik, Field, Form, ErrorMessage } from "formik"
import parsePhoneNumber, { AsYouType } from "libphonenumber-js"
import _keys from "lodash/keys"

import GhostButton from "@src/components/Buttons/GhostButton"
import OrangeButton from "@src/components/Buttons/OrangeButton"

import { countriesList, statesList } from "@src/utilities/list-states-countries"

import type { User, UserUpdateProfileInput } from "@src/entities/types/api"

interface AccountAddressProps {
  user: Partial<User>
  onUpdateProfile: (
    values: UserUpdateProfileInput
  ) => Promise<{ [key: string]: string } | null>
}
export default function AccountAddress({
  user = {},
  onUpdateProfile,
}: AccountAddressProps) {
  const [isEditing, setIsEditing] = React.useState<boolean>(false)

  const formattedAddressLineOne = (): string => {
    if (!user?.defaultAddress?.streetOne) {
      return "Street address not set"
    }
    let address: string = user.defaultAddress.streetOne
    if (
      user.defaultAddress.streetTwo &&
      user.defaultAddress.streetTwo.length > 0
    ) {
      address = `${address}, ${user.defaultAddress.streetTwo}`
    }
    return address
  }

  const formattedAddressLineTwo = (): string => {
    if (
      !user.defaultAddress?.city ||
      user.defaultAddress.city.length === 0 ||
      !user.defaultAddress.state ||
      user.defaultAddress.state.length === 0 ||
      !user.defaultAddress.zip ||
      user.defaultAddress.zip.length === 0 ||
      !user.defaultAddress.country ||
      user.defaultAddress.country.length === 0
    ) {
      return "City, state, and zip not provided"
    }
    return `${user.defaultAddress.city}, ${user.defaultAddress.state} ${user.defaultAddress.zip} ${user.defaultAddress.country}`
  }

  const formattedPhoneNumber = (): string => {
    if (user.phoneNumber && user.phoneNumber.length > 0) {
      const parsedPhoneNumber = parsePhoneNumber(user.phoneNumber, "US")
      if (parsedPhoneNumber) {
        return parsedPhoneNumber.format("NATIONAL")
      }
    }
    return "Phone not provided"
  }

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-extrabold font-browser">Account Details</h3>
        <GhostButton
          onClick={() => setIsEditing((prevState) => !prevState)}
          text={isEditing ? "Cancel" : "Edit"}
          size="small"
        />
      </div>
      {!isEditing && (
        <div className="md:grid md:grid-cols-2 md:gap-4">
          <div>
            <h4 className="font-browser text-lg font-medium">
              Contact Information
            </h4>
            <p>
              {user.firstName} {user.lastName}
            </p>
            <p>{user.email!}</p>
            <p>{formattedPhoneNumber()}</p>
          </div>
          <div>
            <h4 className="font-browser text-lg font-medium">
              Shipping Information
            </h4>
            <ul>
              <li>{formattedAddressLineOne()}</li>
              <li>{formattedAddressLineTwo()}</li>
            </ul>
          </div>
        </div>
      )}
      {isEditing && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            firstName: user.firstName ?? "",
            lastName: user.lastName ?? "",
            phoneNumber: user.phoneNumber ?? "",
            streetOne: user.defaultAddress?.streetOne ?? "",
            streetTwo: user.defaultAddress?.streetTwo ?? "",
            city: user.defaultAddress?.city ?? "",
            state: user.defaultAddress?.state ?? "",
            zip: user.defaultAddress?.zip ?? "",
            country: user.defaultAddress?.country ?? "",
          }}
          onSubmit={(values, { setErrors, setSubmitting }) => {
            onUpdateProfile({
              city: values.city ?? "",
              country: values.country ?? "",
              firstName: values.firstName ?? "",
              lastName: values.lastName ?? "",
              phoneNumber: values.phoneNumber ?? "",
              state: values.state ?? "",
              streetOne: values.streetOne ?? "",
              streetTwo: values.streetTwo ?? "",
              zip: values.zip ?? "",
            }).then((errors) => {
              if (errors && _keys(errors).length) {
                setErrors(errors)
              } else {
                setIsEditing(false)
              }
              setSubmitting(false)
            })
          }}
          validateOnBlur={true}
          validateOnChange={false}
        >
          {(formik) => (
            <Form
              onSubmit={(ev) => {
                ev.preventDefault()
                formik.handleSubmit(ev)
              }}
            >
              <p className="mb-2">
                Need to change your email? Please contact support.
              </p>
              <fieldset disabled={formik.isSubmitting}>
                <div className="md:grid md:grid-cols-2 md:gap-4">
                  <div className="mb-4">
                    <label className="block text-sm mb-1" htmlFor="email">
                      Email Address
                    </label>
                    <input
                      className="rounded border border-gray-400 p-2 text-gray-600 block w-full"
                      disabled={true}
                      readOnly={true}
                      type="email"
                      value={user.email ?? ""}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm mb-1" htmlFor="phoneNumber">
                      Phone Number
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="phoneNumber"
                      onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                        formik.setFieldValue(
                          "phoneNumber",
                          new AsYouType("US").input(ev.target.value)
                        )
                      }}
                      type="text"
                    />
                    <ErrorMessage name="phoneNumber">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
                <h4 className="font-browser font-medium mb-2">
                  Shipping Information
                </h4>
                <div className="md:grid md:grid-cols-2 md:gap-4">
                  <div className="mb-2">
                    <label className="block text-sm mb-1" htmlFor="firstName">
                      First Name
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="firstName"
                      type="text"
                    />
                    <ErrorMessage name="firstName">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm mb-1" htmlFor="lastName">
                      Last Name
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="lastName"
                      type="text"
                    />
                    <ErrorMessage name="lastName">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
                <div className="mb-2">
                  <label className="block text-sm mb-1" htmlFor="country">
                    Country
                  </label>
                  <Field
                    className="rounded border border-gray-400 p-2 block w-full"
                    as="select"
                    name="country"
                  >
                    {countriesList.map((country) => (
                      <option
                        key={`country_${country.abbreviation}`}
                        value={country.abbreviation}
                      >
                        {country.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="country">
                    {(msg) => (
                      <div className="mt-1 text-red text-sm">{msg}</div>
                    )}
                  </ErrorMessage>
                </div>
                <div className="md:grid md:grid-cols-3 md:gap-4">
                  <div className="mb-2 md:col-span-2">
                    <label
                      className="block text-sm mb-1"
                      htmlFor="streetAddressOne"
                    >
                      Street Address
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="streetOne"
                      type="text"
                    />
                    <ErrorMessage name="streetOne">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm mb-1" htmlFor="streetTwo">
                      Unit/Apt
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="streetTwo"
                      placeholder="None"
                      type="text"
                    />
                  </div>
                </div>
                <div className="md:grid md:grid-cols-5 md:gap-4">
                  <div className="mb-2 md:col-span-2">
                    <label className="block text-sm mb-1" htmlFor="city">
                      City
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="city"
                      type="text"
                    />
                    <ErrorMessage name="city">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="mb-2 md:col-span-2">
                    <label className="block text-sm mb-1" htmlFor="state">
                      State
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      as="select"
                      name="state"
                    >
                      <option disabled={true} value="" />
                      {statesList.map((state) => (
                        <option
                          key={`state_${state.abbreviation}`}
                          value={state.abbreviation}
                        >
                          {state.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="state">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="form-column form-column-quarter">
                    <label className="block text-sm mb-1" htmlFor="zip">
                      ZIP Code
                    </label>
                    <Field
                      className="rounded border border-gray-400 p-2 block w-full"
                      name="zip"
                      type="text"
                    />
                    <ErrorMessage name="zip">
                      {(msg) => (
                        <div className="mt-1 text-red text-sm">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
                <div className="mb-8 pt-4 text-center">
                  <OrangeButton
                    onClick={() =>
                      formik.validateForm().then(() => formik.handleSubmit())
                    }
                    text="Update My Account"
                  />
                </div>
              </fieldset>
            </Form>
          )}
        </Formik>
      )}
    </section>
  )
}
